import { createModel } from "@rematch/core";
import type { RootModel } from ".";
import { LERC20Abi } from "../../abis";
import { getContract } from "../../utils/getContract";
import LERC20_ABI from "../../abis/LERC20.abi.json";
import { DISPERSERAbi } from "../../abis/DISPERSERAbi";
import DISPERSER_ABI from "../../abis/DISPERSER.abi.json";
import { Web3Provider } from "@ethersproject/providers";
import { BigNumber, ethers, utils } from "ethers";
import { ChainData } from "../../config/web3";
import { MaxUint256 } from "@ethersproject/constants";
import { TxStatus } from "../../types";

type DisperseModel = {
  losslessTokenContract: LERC20Abi | null;
  disperserContract: DISPERSERAbi | null;
  tokenInfo: {
    name: string | null | undefined;
    symbol: string | null | undefined;
    decimals: number | null | undefined;
    balance: string | null | undefined;
    tokenAddress: string | null | undefined;
    isLossless: boolean;
  };
  isSpendApproved: boolean;
  estimateAirdropFee: number | undefined | null;
  failedTransactions: ethers.ContractTransaction[];
  txHashes: {
    [key: number]: {
      txHash: string | null | undefined;
      status: TxStatus | null | undefined;
      batchInfo: {
        addresses: string[];
        amounts: BigNumber[];
      };
    };
  };
  txCount: number;
  aidropCompleted: boolean;
  airDropBatches: {
    [key: number]: {
      addresses: string[];
      amounts: BigNumber[];
    };
  };
};

export const disperse = createModel<RootModel>()({
  state: {
    losslessTokenContract: null,
    disperserContract: null,
    tokenInfo: {
      name: null,
      symbol: null,
      decimals: null,
      balance: null,
      tokenAddress: null,
      isLossless: false,
    },
    isSpendApproved: false,
    estimateAirdropFee: null,
    airDropBatches: {},
    txHashes: {},
    txCount: 0,
    aidropCompleted: false,
  } as DisperseModel,
  reducers: {
    setContracts: (
      state,
      payload: {
        losslessTokenContract: LERC20Abi;
        disperserContract: DISPERSERAbi;
      }
    ) => {
      return {
        ...state,
        losslessTokenContract: payload.losslessTokenContract,
        disperserContract: payload.disperserContract,
      };
    },
    setTokenContractInfo: (
      state,
      payload: {
        name: string;
        symbol: string;
        decimals: number;
        balance: string;
        tokenAddress: string;
      }
    ) => {
      return {
        ...state,
        tokenInfo: {
          ...state.tokenInfo,
          ...payload,
        },
      };
    },
    setIsSpendApproved: (state, payload: boolean) => {
      return {
        ...state,
        isSpendApproved: payload,
      };
    },
    setEstimateAirdropFee: (state, payload: number) => {
      return {
        ...state,
        estimateAirdropFee: payload,
      };
    },

    addFailedTransaction: (state, payload: ethers.ContractTransaction) => {
      return {
        ...state,
        failedTransactions: [...state.failedTransactions, payload],
      };
    },
    addTxHash: (
      state,
      payload: {
        id: number;
        txHash: string | null | undefined;
        status: TxStatus | null | undefined;
        batchInfo: {
          addresses: string[];
          amounts: BigNumber[];
        };
      }
    ) => {
      if (state.txCount === 0) return;
      return {
        ...state,
        txHashes: {
          ...state.txHashes,
          [payload.id]: {
            txHash: payload.txHash,
            status: payload.status,
            batchInfo: payload.batchInfo,
          },
        },
      };
    },
    setAirDropBatches: (
      state,
      payload: {
        [key: number]: {
          addresses: string[];
          amounts: BigNumber[];
        };
      }
    ) => {
      return {
        ...state,
        airDropBatches: payload,
      };
    },

    setTxCount: (state, payload: number) => {
      return {
        ...state,
        txCount: payload,
      };
    },

    clearTxHashes: (state) => {
      return {
        ...state,
        txHashes: {},
      };
    },
    setAidropCompleted: (state, payload: boolean) => {
      return {
        ...state,
        aidropCompleted: payload,
      };
    },
    resetState: (state) => {
      return {
        ...state,
        losslessTokenContract: null,
        disperserContract: null,
        tokenInfo: {
          name: null,
          symbol: null,
          decimals: null,
          balance: null,
          tokenAddress: null,
          isLossless: false,
        },
        isSpendApproved: false,
        airDropBatches: {},
        estimateAirdropFee: null,
        txHashes: {},
        txCount: 0,
        aidropCompleted: false,
      };
    },

    resetTokenInfo: (state) => {
      return {
        ...state,
        tokenInfo: {
          name: null,
          symbol: null,
          decimals: null,
          balance: null,
          tokenAddress: null,
          isLossless: false,
        },
      };
    },

    setIsLossless: (state, payload: boolean) => {
      return {
        ...state,
        tokenInfo: {
          ...state.tokenInfo,
          isLossless: payload,
        },
      };
    },
  },
  effects: (dispatch) => ({
    async initContracts({
      tokenAddress,
      library,
      account,
      chainId,
    }: {
      tokenAddress: string;
      library: Web3Provider;
      account: string;
      chainId: number;
    }) {
      const losslessTokenContract = getContract(
        tokenAddress,
        LERC20_ABI,
        library,
        account
      ) as LERC20Abi;
      const disperserContract = getContract(
        ChainData[chainId].dispersAddress,
        DISPERSER_ABI,
        library,
        account
      ) as DISPERSERAbi;

      dispatch.disperse.setContracts({
        losslessTokenContract,
        disperserContract,
      });
    },

    async fetchTokenInfo(
      payload: { account: string; library: Web3Provider },
      state
    ) {
      dispatch.disperse.resetTokenInfo();
      const { losslessTokenContract } = state.disperse;
      if (!losslessTokenContract) {
        return;
      }

      const tokenName = await losslessTokenContract.name();
      const tokenSymbol = await losslessTokenContract.symbol();
      const tokenDecimals = await losslessTokenContract.decimals();
      const tokenBalance = await losslessTokenContract.balanceOf(
        payload.account
      );

      const byteCode = await payload.library.getCode(
        losslessTokenContract.address
      );

      if (byteCode.includes(ethers.utils.id("isLosslessOn()").slice(2, 10))) {
        dispatch.disperse.setIsLossless(true);
      }

      const tokenInfo = {
        name: tokenName,
        symbol: tokenSymbol,
        decimals: tokenDecimals,
        balance: utils.formatUnits(tokenBalance, tokenDecimals).toString(),
        tokenAddress: losslessTokenContract.address,
      };

      dispatch.disperse.setTokenContractInfo(tokenInfo);
    },

    async checkAllowance(payload: { account: string; chainId: number }, state) {
      const { losslessTokenContract } = state.disperse;
      if (!losslessTokenContract) {
        return;
      }

      const allowance = await losslessTokenContract.allowance(
        payload.account,
        ChainData[payload.chainId].dispersAddress
      );

      dispatch.disperse.setIsSpendApproved(allowance.gt(0));
    },

    async approveSpend(
      payload: { account: string; chainId: number },
      state
    ): Promise<ethers.ContractReceipt | undefined> {
      const { losslessTokenContract } = state.disperse;
      if (!losslessTokenContract) {
        return;
      }

      const tx = await losslessTokenContract.approve(
        ChainData[payload.chainId].dispersAddress,
        MaxUint256
      );

      const receipt = await tx.wait();

      if (receipt) {
        return receipt;
      }
    },

    async prepareAirdrop(
      payload: { account: string; library: Web3Provider },
      state
    ) {
      if (!state.disperse.losslessTokenContract) {
        return;
      }
      const snapshotData = state.snapshotModel.snapshotData;
      if (!snapshotData) {
        return;
      }
      const disperseToken = state.disperse.tokenInfo.tokenAddress;
      const disperseContract = state.disperse.disperserContract;
      dispatch.disperse.clearTxHashes();

      // TODO (@themeltdown): this is some test code, to get all of the balances for users. Remove this later.

      // let receivedAddresses = [];
      // let promises = [];
      // for (let i = 0; i < snapshotData.length; i++) {
      //   const balance = state.disperse.losslessTokenContract.balanceOf(
      //     snapshotData[i].wallet
      //   );
      //   promises.push(balance);
      // }
      // const balances = await Promise.all(promises);
      // for (let i = 0; i < snapshotData.length; i++) {
      //   if (balances[i].gt(0)) {
      //     receivedAddresses.push(snapshotData[i].wallet);
      //   }
      // }
      // console.log("receivedAddresses", receivedAddresses.length);

      if (!disperseContract || !disperseToken || !snapshotData) {
        return;
      }

      const ONE_ADDRESS_TX_FEE =
        await disperseContract.estimateGas.disperseToken(
          disperseToken,
          [payload.account],
          [ethers.utils.parseEther("1") as BigNumber],
          {
            from: payload.account,
          }
        );

      const signerChainId = await payload.library.getNetwork();
      const TX_CHUNK = ChainData[signerChainId.chainId].addressesPerTransaction;

      const estimateGasFee =
        ONE_ADDRESS_TX_FEE.toNumber() * snapshotData.length;
      const aidropAddresses = snapshotData?.map((item) => item.wallet);
      const aidropAmounts = snapshotData?.map((item) =>
        ethers.utils.parseEther(item.balance)
      ) as BigNumber[];
      dispatch.disperse.setEstimateAirdropFee(estimateGasFee);

      const batches: {
        [key: number]: {
          addresses: string[];
          amounts: BigNumber[];
        };
      } = [];
      for (let i = 0; i < aidropAddresses.length; i += TX_CHUNK) {
        const batch = aidropAddresses.slice(i, i + TX_CHUNK);
        const batchAmounts = aidropAmounts.slice(i, i + TX_CHUNK);
        batches[i] = {
          addresses: batch,
          amounts: batchAmounts,
        };
      }

      dispatch.disperse.setTxCount(Object.keys(batches).length);
      dispatch.disperse.setAirDropBatches(batches);
    },

    async commitAirdrop(payload: { account: string }, state) {
      const { airDropBatches, txCount, disperserContract, tokenInfo } =
        state.disperse;
      if (txCount === 0) return;

      if (txCount && disperserContract && tokenInfo.tokenAddress) {
        for (const [index, batch] of Object.entries(airDropBatches)) {
          try {
            const txHash = await disperserContract.disperseToken(
              tokenInfo.tokenAddress,
              batch.addresses,
              batch.amounts,
              {
                from: payload.account,
              }
            );
            await txHash.wait();
            dispatch.disperse.addTxHash({
              id: Number(index),
              txHash: txHash.hash,
              status: TxStatus.PROCESSED,
              batchInfo: {
                addresses: batch.addresses,
                amounts: batch.amounts,
              },
            });
          } catch (e) {
            console.log(e);
            dispatch.disperse.addTxHash({
              id: Number(index),
              txHash: null,

              status: TxStatus.FAILED,
              batchInfo: {
                addresses: batch.addresses,
                amounts: batch.amounts,
              },
            });
          }
        }
      }
    },

    async retryAirdrop(payload: { account: string; chainId: number }, state) {
      const { txHashes } = state.disperse;
      const disperseContract = state.disperse.disperserContract;
      const disperseToken = state.disperse.tokenInfo.tokenAddress;
      const oldTxData = txHashes;
      dispatch.disperse.clearTxHashes();
      if (!disperseContract || !disperseToken) return null;

      const failedTxCount = Object.values(oldTxData).filter(
        (txHash) => txHash.txHash === null
      ).length;

      dispatch.disperse.setTxCount(failedTxCount);
      if (failedTxCount > 0) {
        for (const [index, txHash] of Object.entries(oldTxData)) {
          if (txHash.txHash === null) {
            try {
              const tx = await disperseContract.disperseToken(
                disperseToken,
                txHash.batchInfo.addresses,
                txHash.batchInfo.amounts,
                {
                  from: payload.account,
                }
              );
              await tx.wait();
              dispatch.disperse.addTxHash({
                id: Number(index),
                txHash: tx.hash,
                status: TxStatus.PROCESSED,
                batchInfo: {
                  addresses: txHash.batchInfo.addresses,
                  amounts: txHash.batchInfo.amounts,
                },
              });
            } catch (e) {
              console.log(e);
              dispatch.disperse.addTxHash({
                id: Number(index),
                txHash: null,

                status: TxStatus.FAILED,
                batchInfo: {
                  addresses: txHash.batchInfo.addresses,
                  amounts: txHash.batchInfo.amounts,
                },
              });
            }
          }
        }
      }
    },
  }),
});
